import React, { useState, useEffect, useCallback } from 'react';
import { 
    Box, Card, CardHeader, Button, Typography, Table, TableBody, 
    TableCell, TableContainer, TableHead, TableRow, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    Alert, IconButton, Tooltip, Chip, MenuItem, Select, FormControl, InputLabel, OutlinedInput
} from '@mui/material';
import { 
    Add as AddIcon, Refresh as RefreshIcon, Delete as DeleteIcon, 
    Key as KeyIcon, Edit as EditIcon, Person as PersonIcon 
} from '@mui/icons-material';
import { 
    getUsers, createUser, updateUser, deleteUser, 
    regenerateUserAPIKey, getPartners 
} from './api';
import { useAuth } from './AuthContext';

function UsersPanel({ apiKey, onError }) {
    const { isAdmin } = useAuth();
    const [users, setUsers] = useState([]);
    const [partners, setPartners] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [refreshKey, setRefreshKey] = useState(0);
    
    // Dialog states
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [openKeyDialog, setOpenKeyDialog] = useState(false);
    
    // Form states
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        role: 'Partner',
        partnerIds: [] // Changed from partnerId to partnerIds (array)
    });
    const [editingUser, setEditingUser] = useState(null);
    const [addingUser, setAddingUser] = useState(false);
    const [newApiKey, setNewApiKey] = useState('');
    
    // Fetch users
    const fetchUsers = useCallback(async () => {
        if (!apiKey) return;
        
        setLoading(true);
        setError('');
        try {
            const data = await getUsers(apiKey);
            setUsers(data || []);
            if(onError) onError(null);
        } catch (err) {
            console.error("Failed to fetch users:", err);
            const errorMsg = err.data?.error || err.message || "Failed to fetch users";
            setError(`Error: ${errorMsg} (Status: ${err.status || 'N/A'})`);
            if(onError) onError(errorMsg);
        } finally {
            setLoading(false);
        }
    }, [apiKey, onError]);
    
    // Fetch partners for dropdown
    const fetchPartners = useCallback(async () => {
        if (!apiKey) return;
        
        try {
            const data = await getPartners(apiKey);
            setPartners(data || []);
        } catch (err) {
            console.error("Failed to fetch partners:", err);
        }
    }, [apiKey]);
    
    useEffect(() => {
        fetchUsers();
        fetchPartners();
    }, [fetchUsers, fetchPartners, refreshKey]);
    
    // Handle refresh
    const handleRefresh = () => {
        setRefreshKey(prev => prev + 1);
    };
    
    // Reset form
    const resetForm = () => {
        setFormData({
            name: '',
            email: '',
            role: 'Partner',
            partnerIds: []
        });
        setEditingUser(null);
    };
    
    // Handle create user
    const handleCreateUser = async () => {
        if (!formData.name.trim() || !formData.email.trim()) return;
        
        // Validate partner selection for Partner role
        if (formData.role === 'Partner' && (!formData.partnerIds || formData.partnerIds.length === 0)) {
            setError('Please select at least one partner for Partner role users');
            return;
        }
        
        setAddingUser(true);
        setError('');
        try {
            const userData = {
                name: formData.name,
                email: formData.email,
                role: formData.role,
                partnerIds: formData.role === 'Partner' ? formData.partnerIds : []
            };
            
            const result = await createUser(apiKey, userData);
            setNewApiKey(result.apiKey);
            setOpenAddDialog(false);
            setOpenKeyDialog(true);
            resetForm();
            handleRefresh();
        } catch (err) {
            console.error("Failed to create user:", err);
            const errorMsg = err.data?.error || err.message || "Failed to create user";
            setError(`Error: ${errorMsg} (Status: ${err.status || 'N/A'})`);
        } finally {
            setAddingUser(false);
        }
    };
    
    // Handle edit user
    const handleEditClick = (user) => {
        setEditingUser(user);
        // Handle both old partnerId and new partnerIds format
        let partnerIds = [];
        if (user.partnerIds && Array.isArray(user.partnerIds)) {
            partnerIds = user.partnerIds;
        } else if (user.partnerId) {
            partnerIds = [user.partnerId];
        }
        setFormData({
            name: user.name,
            email: user.email,
            role: user.role,
            partnerIds: partnerIds
        });
        setOpenEditDialog(true);
    };
    
    const handleUpdateUser = async () => {
        if (!editingUser || !formData.name.trim() || !formData.email.trim()) return;
        
        // Validate partner selection for Partner role
        if (formData.role === 'Partner' && (!formData.partnerIds || formData.partnerIds.length === 0)) {
            setError('Please select at least one partner for Partner role users');
            return;
        }
        
        setAddingUser(true);
        setError('');
        try {
            const userData = {
                name: formData.name,
                email: formData.email,
                role: formData.role,
                partnerIds: formData.role === 'Partner' ? formData.partnerIds : []
            };
            
            await updateUser(apiKey, editingUser.id, userData);
            setOpenEditDialog(false);
            resetForm();
            handleRefresh();
        } catch (err) {
            console.error("Failed to update user:", err);
            const errorMsg = err.data?.error || err.message || "Failed to update user";
            setError(`Error: ${errorMsg} (Status: ${err.status || 'N/A'})`);
        } finally {
            setAddingUser(false);
        }
    };
    
    // Handle delete user
    const handleDeleteUser = async (userId, userName) => {
        if (!window.confirm(`Are you sure you want to delete user "${userName}"?`)) return;
        
        setLoading(true);
        setError('');
        try {
            await deleteUser(apiKey, userId);
            handleRefresh();
        } catch (err) {
            console.error(`Failed to delete user ${userId}:`, err);
            const errorMsg = err.data?.error || err.message || `Failed to delete user ${userName}`;
            setError(`Error: ${errorMsg} (Status: ${err.status || 'N/A'})`);
        } finally {
            setLoading(false);
        }
    };
    
    // Handle regenerate API key
    const handleRegenerateKey = async (userId, userName) => {
        if (!window.confirm(`Are you sure you want to regenerate the API key for user "${userName}"? The old key will become invalid.`)) return;
        
        setLoading(true);
        setError('');
        try {
            const result = await regenerateUserAPIKey(apiKey, userId);
            setNewApiKey(result.apiKey);
            setOpenKeyDialog(true);
            handleRefresh();
        } catch (err) {
            console.error(`Failed to regenerate key for user ${userId}:`, err);
            const errorMsg = err.data?.error || err.message || `Failed to regenerate key for user ${userName}`;
            setError(`Error: ${errorMsg} (Status: ${err.status || 'N/A'})`);
        } finally {
            setLoading(false);
        }
    };
    
    const getRoleBadgeColor = (role) => {
        switch(role) {
            case 'Admin': return 'error';
            case 'Dev': return 'warning';
            case 'Partner': return 'info';
            default: return 'default';
        }
    };
    
    const getPartnerName = (partnerId) => {
        const partner = partners.find(p => p.id === partnerId);
        return partner ? partner.name : 'N/A';
    };
    
    const getPartnerNames = (partnerIds) => {
        if (!partnerIds || partnerIds.length === 0) return [];
        return partnerIds.map(id => ({
            id,
            name: getPartnerName(id)
        }));
    };
    
    return (
        <Card elevation={2} sx={{ overflow: 'hidden', mt: 3 }}>
            <CardHeader
                avatar={<PersonIcon color="primary" />}
                title={<Typography variant="h6">Users</Typography>}
                action={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {isAdmin && (
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => setOpenAddDialog(true)}
                                size="small"
                            >
                                Add User
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={handleRefresh}
                            disabled={loading}
                            size="small"
                        >
                            Refresh
                        </Button>
                    </Box>
                }
            />
            
            <Box sx={{ px: 2, pb: 2 }}>
                {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>}
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                
                {!loading && !error && (
                    <TableContainer sx={{ maxHeight: 500 }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Role</TableCell>
                                    <TableCell>Partner</TableCell>
                                    <TableCell>Created At</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {users.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            <Typography color="textSecondary" sx={{ py: 2 }}>
                                                No users found
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    users.map((user) => (
                                        <TableRow key={user.id}>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {user.name}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">
                                                    {user.email}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip 
                                                    label={user.role} 
                                                    size="small" 
                                                    color={getRoleBadgeColor(user.role)}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {(user.partnerIds && user.partnerIds.length > 0) ? (
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        {getPartnerNames(user.partnerIds).map((partner) => (
                                                            <Chip 
                                                                key={partner.id}
                                                                label={partner.name} 
                                                                size="small" 
                                                                variant="outlined"
                                                                color="default"
                                                            />
                                                        ))}
                                                    </Box>
                                                ) : (user.partnerId ? getPartnerName(user.partnerId) : '-')}
                                            </TableCell>
                                            <TableCell>
                                                {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '-'}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                                    <Tooltip title="Regenerate API Key">
                                                        <IconButton 
                                                            size="small" 
                                                            color="primary" 
                                                            onClick={() => handleRegenerateKey(user.id, user.name)}
                                                            disabled={!isAdmin}
                                                        >
                                                            <KeyIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    {isAdmin && (
                                                        <>
                                                            <Tooltip title="Edit User">
                                                                <IconButton 
                                                                    size="small" 
                                                                    color="info" 
                                                                    onClick={() => handleEditClick(user)}
                                                                >
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Delete User">
                                                                <IconButton 
                                                                    size="small" 
                                                                    color="error" 
                                                                    onClick={() => handleDeleteUser(user.id, user.name)}
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>
            
            {/* Add User Dialog */}
            <Dialog 
                open={openAddDialog} 
                onClose={() => { setOpenAddDialog(false); resetForm(); }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Add New User</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            autoFocus
                            label="Name"
                            fullWidth
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            disabled={addingUser}
                        />
                        <TextField
                            label="Email"
                            type="email"
                            fullWidth
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            disabled={addingUser}
                        />
                        <FormControl fullWidth>
                            <InputLabel>Role</InputLabel>
                            <Select
                                value={formData.role}
                                label="Role"
                                onChange={(e) => setFormData({ ...formData, role: e.target.value, partnerIds: [] })}
                                disabled={addingUser}
                            >
                                <MenuItem value="Admin">Admin</MenuItem>
                                <MenuItem value="Dev">Dev</MenuItem>
                                <MenuItem value="Partner">Partner</MenuItem>
                            </Select>
                        </FormControl>
                        {formData.role === 'Partner' && (
                            <FormControl fullWidth>
                                <InputLabel>Partners</InputLabel>
                                <Select
                                    multiple
                                    value={formData.partnerIds}
                                    label="Partners"
                                    onChange={(e) => setFormData({ ...formData, partnerIds: e.target.value })}
                                    disabled={addingUser}
                                    input={<OutlinedInput label="Partners" />}
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((value) => (
                                                <Chip 
                                                    key={value} 
                                                    label={getPartnerName(value)} 
                                                    size="small"
                                                />
                                            ))}
                                        </Box>
                                    )}
                                >
                                    {partners.map((partner) => (
                                        <MenuItem key={partner.id} value={partner.id}>
                                            {partner.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setOpenAddDialog(false); resetForm(); }} disabled={addingUser}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleCreateUser} 
                        color="primary" 
                        disabled={!formData.name.trim() || !formData.email.trim() || addingUser}
                        variant="contained"
                    >
                        {addingUser ? <CircularProgress size={24} /> : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
            
            {/* Edit User Dialog */}
            <Dialog 
                open={openEditDialog} 
                onClose={() => { setOpenEditDialog(false); resetForm(); }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Edit User</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            autoFocus
                            label="Name"
                            fullWidth
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            disabled={addingUser}
                        />
                        <TextField
                            label="Email"
                            type="email"
                            fullWidth
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            disabled={addingUser}
                        />
                        <FormControl fullWidth>
                            <InputLabel>Role</InputLabel>
                            <Select
                                value={formData.role}
                                label="Role"
                                onChange={(e) => setFormData({ ...formData, role: e.target.value, partnerIds: [] })}
                                disabled={addingUser}
                            >
                                <MenuItem value="Admin">Admin</MenuItem>
                                <MenuItem value="Dev">Dev</MenuItem>
                                <MenuItem value="Partner">Partner</MenuItem>
                            </Select>
                        </FormControl>
                        {formData.role === 'Partner' && (
                            <FormControl fullWidth>
                                <InputLabel>Partners</InputLabel>
                                <Select
                                    multiple
                                    value={formData.partnerIds}
                                    label="Partners"
                                    onChange={(e) => setFormData({ ...formData, partnerIds: e.target.value })}
                                    disabled={addingUser}
                                    input={<OutlinedInput label="Partners" />}
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((value) => (
                                                <Chip 
                                                    key={value} 
                                                    label={getPartnerName(value)} 
                                                    size="small"
                                                />
                                            ))}
                                        </Box>
                                    )}
                                >
                                    {partners.map((partner) => (
                                        <MenuItem key={partner.id} value={partner.id}>
                                            {partner.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setOpenEditDialog(false); resetForm(); }} disabled={addingUser}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleUpdateUser} 
                        color="primary" 
                        disabled={!formData.name.trim() || !formData.email.trim() || addingUser}
                        variant="contained"
                    >
                        {addingUser ? <CircularProgress size={24} /> : 'Update'}
                    </Button>
                </DialogActions>
            </Dialog>
            
            {/* API Key Dialog */}
            <Dialog 
                open={openKeyDialog} 
                onClose={() => setOpenKeyDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>API Key Generated</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Save this API key now! It will not be displayed again.
                    </Alert>
                    <TextField
                        fullWidth
                        value={newApiKey}
                        InputProps={{
                            readOnly: true,
                        }}
                        variant="filled"
                        sx={{ mb: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => {
                            navigator.clipboard.writeText(newApiKey);
                            alert('API key copied to clipboard');
                        }} 
                        color="primary"
                    >
                        Copy to Clipboard
                    </Button>
                    <Button onClick={() => setOpenKeyDialog(false)} variant="contained" color="primary">
                        Done
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
}

export default UsersPanel;

